/* global CustomFunctions */
import { getValidToken } from "../shared/auth-service";
import { addWorkingDays, getHolidaysTable, getWorkingDaysCount, getWorkingDaysFromTable, getWorkingDaysTable } from "./calendar/holidays-service";
import { handleScalarError, handleTableError } from "./errors";
import { getLoanDebtFlowTable } from "./loans/loan-flow-service";
import { bmfTerm, cdi, cdiImplicito, forwardCurve, ipca, ipcaImplicito, ptax, ptaxD1, spotFx } from "./markets/markets-service";
import { impliedVolFx, mtmFx, priceFx } from "./pricing/pricing-service";

async function resolveToken(): Promise<void> {
  const token = await getValidToken();
  if (!token) {
    throw new CustomFunctions.Error(
      CustomFunctions.ErrorCode.invalidValue,
      "Sessao expirada ou nao iniciada. Abra o painel lateral e faca login."
    );
  }
}

/**
 * Precifica estrutura FX usando curvas Cognito e vol B3.
 * @customfunction PRICE_FX
 * @param tipoInstrumento Instrumento derivativo. Use: NDFVanilla, NDFCAP, CallSpread, CallVanilla, PutVanilla, PutSpread, Straddle, Strangle, Collar ou Fence.
 * @param moeda Moeda do instrumento. Use USD, EUR, JPY ou CHF.
 * @param dataVencimento Data de vencimento. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [strike] Strike 1 em BRL/moeda. Obrigatorio para opcoes. Ex: strike da call em CallVanilla ou perna comprada em CallSpread.
 * @param [strike2] Strike 2 em BRL/moeda. Use em estruturas com duas pernas, como CallSpread, PutSpread, Strangle, Collar e Fence.
 * @param [dataBase] Data base do calculo. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, usa o ultimo dia util pela API de feriados.
 * @param [spotManual] Spot manual em BRL/moeda. Se vazio, usa o spot/PTAX de mercado da Cognito.
 * @param [carryManual] Taxa carry FX manual em percentual ao ano. Ex: 5,25 para 5,25% a.a.
 * @param [preManual] Taxa pre BRL manual em percentual ao ano. Ex: 12,15 para 12,15% a.a.
 * @param [volManual] Volatilidade manual em percentual ao ano. Ex: 15 para 15% a.a.
 * @returns NDF retorna taxa forward. Opcoes retornam premio/custo unitario conforme o instrumento.
 */
export async function PRICE_FX(
  tipoInstrumento: string,
  moeda: string,
  dataVencimento: any,
  strike?: number,
  strike2?: number,
  dataBase?: any,
  spotManual?: number,
  carryManual?: number,
  preManual?: number,
  volManual?: number
): Promise<number | string> {
  try {
    await resolveToken();
    return await priceFx(tipoInstrumento, moeda, dataVencimento, strike, strike2, dataBase, spotManual, carryManual, preManual, volManual);
  } catch (e) {
    return handleScalarError("PRICE_FX", e);
  }
}

/**
 * Calcula o MtM de uma estrutura FX existente.
 * @customfunction MTM_FX
 * @param tipoInstrumento Instrumento derivativo. Use: NDFVanilla, NDFCAP, CallSpread, CallVanilla, PutVanilla, PutSpread, Straddle, Strangle, Collar ou Fence.
 * @param moeda Moeda do instrumento. Use USD, EUR, JPY ou CHF.
 * @param dataVencimento Data de vencimento. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param taxaContratada Taxa/preco contratado da operacao. Para NDF, informe a taxa forward contratada em BRL/moeda.
 * @param [notional] Valor nocional na moeda do instrumento. Ex: 1000000 para USD 1 MM.
 * @param [strike] Strike 1 em BRL/moeda. Use para opcoes e estruturas com strike.
 * @param [strike2] Strike 2 em BRL/moeda. Use em estruturas com duas pernas, como CallSpread, PutSpread, Strangle, Collar e Fence.
 * @param [dataContratacao] Data de contratacao. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, a API usa o padrao do calculo.
 * @param [dataBase] Data base do calculo. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, usa o ultimo dia util pela API de feriados.
 * @param [spotManual] Spot manual em BRL/moeda. Se vazio, usa o spot/PTAX de mercado da Cognito.
 * @param [carryManual] Taxa carry FX manual em percentual ao ano. Ex: 5,25 para 5,25% a.a.
 * @param [preManual] Taxa pre BRL manual em percentual ao ano. Ex: 12,15 para 12,15% a.a.
 * @param [volManual] Volatilidade manual em percentual ao ano. Ex: 15 para 15% a.a.
 * @returns MtM liquido da estrutura, normalmente em BRL.
 */
export async function MTM_FX(
  tipoInstrumento: string,
  moeda: string,
  dataVencimento: any,
  taxaContratada: number,
  notional?: number,
  strike?: number,
  strike2?: number,
  dataContratacao?: any,
  dataBase?: any,
  spotManual?: number,
  carryManual?: number,
  preManual?: number,
  volManual?: number
): Promise<number | string> {
  try {
    await resolveToken();
    return await mtmFx(tipoInstrumento, moeda, dataVencimento, taxaContratada, notional, strike, strike2, dataContratacao, dataBase, spotManual, carryManual, preManual, volManual);
  } catch (e) {
    return handleScalarError("MTM_FX", e);
  }
}

/**
 * Calcula a volatilidade implicita de uma estrutura FX.
 * @customfunction IMPLIED_VOL_FX
 * @param tipoInstrumento Instrumento derivativo de opcao. Use CallSpread, CallVanilla, PutVanilla, PutSpread, Straddle, Strangle, Collar, Fence ou NDFCAP. NDFVanilla nao tem volatilidade implicita.
 * @param moeda Moeda do instrumento. Use USD, EUR, JPY ou CHF.
 * @param dataVencimento Data de vencimento. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param strike Strike 1 em BRL/moeda.
 * @param [strike2] Strike 2 em BRL/moeda. Use em estruturas com duas pernas, como CallSpread, PutSpread, Strangle, Collar e Fence.
 * @param [notional] Valor nocional na moeda do instrumento. Ex: 1000000 para USD 1 MM.
 * @param [premioUnitario] Premio unitario observado no mercado. Use quando tiver o premio por unidade.
 * @param [premioTotal] Premio total observado em BRL. Use quando tiver o valor financeiro total do premio.
 * @param [taxaContratada] Taxa contratada em BRL/moeda. Usada em estruturas como NDFCAP.
 * @param [dataBase] Data base do calculo. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, usa o ultimo dia util pela API de feriados.
 * @returns Volatilidade implicita em percentual ao ano. Ex: 15 significa 15% a.a.
 */
export async function IMPLIED_VOL_FX(
  tipoInstrumento: string,
  moeda: string,
  dataVencimento: any,
  strike: number,
  strike2?: number,
  notional?: number,
  premioUnitario?: number,
  premioTotal?: number,
  taxaContratada?: number,
  dataBase?: any
): Promise<number | string> {
  try {
    await resolveToken();
    return await impliedVolFx(tipoInstrumento, moeda, dataVencimento, strike, strike2, notional, premioUnitario, premioTotal, taxaContratada, dataBase);
  } catch (e) {
    return handleScalarError("IMPLIED_VOL_FX", e);
  }
}

/**
 * Monta o Fluxo da Divida de uma operacao pelo sequential id.
 * @customfunction LOAN_DEBT_FLOW
 * @param idOperacao Identificador sequencial da operacao de credito. Aceita LOAN1340 ou apenas 1340.
 * @returns Tabela com parcelas, vencimentos, saldo devedor, amortizacao, juros, cambio, valores em BRL e status.
 */
export async function LOAN_DEBT_FLOW(idOperacao: string): Promise<any[][]> {
  try {
    await resolveToken();
    return getLoanDebtFlowTable(idOperacao);
  } catch (e) {
    return handleTableError("LOAN_DEBT_FLOW", e);
  }
}

/**
 * Retorna a tabela de feriados a partir de uma data inicial, com data final opcional.
 * @customfunction HOLIDAYS
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [dataFinal] Data final opcional. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, busca os feriados disponiveis apos a data inicial.
 * @returns Tabela com data e dia da semana dos feriados.
 */
export async function HOLIDAYS(dataInicial: any, dataFinal?: any): Promise<any[][]> {
  try {
    await resolveToken();
    return await getHolidaysTable(dataInicial, dataFinal);
  } catch (e) {
    return handleTableError("HOLIDAYS", e);
  }
}

/**
 * Retorna a tabela de dias uteis em um intervalo, excluindo finais de semana e feriados.
 * @customfunction WORKING_DAYS
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param dataFinal Data final. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @returns Tabela com data e dia da semana dos dias uteis.
 */
export async function WORKING_DAYS(dataInicial: any, dataFinal: any): Promise<any[][]> {
  try {
    await resolveToken();
    return await getWorkingDaysTable(dataInicial, dataFinal);
  } catch (e) {
    return handleTableError("WORKING_DAYS", e);
  }
}

/**
 * Retorna a quantidade de dias uteis em um intervalo, excluindo finais de semana e feriados.
 * @customfunction WORKING_DAYS_COUNT
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param dataFinal Data final. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @returns Numero de dias uteis no intervalo.
 */
export async function WORKING_DAYS_COUNT(dataInicial: any, dataFinal: any): Promise<number | string> {
  try {
    await resolveToken();
    return await getWorkingDaysCount(dataInicial, dataFinal);
  } catch (e) {
    return handleScalarError("WORKING_DAYS_COUNT", e);
  }
}

/**
 * Soma ou subtrai dias uteis de uma data, considerando finais de semana e feriados do calendario Cognito.
 * @customfunction ADD_WORKING_DAYS
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param quantidadeDiasUteis Quantidade inteira de dias uteis. Use valor negativo para subtrair dias.
 * @returns Data final no formato DD/MM/AAAA.
 */
export async function ADD_WORKING_DAYS(dataInicial: any, quantidadeDiasUteis: number): Promise<string> {
  try {
    await resolveToken();
    return await addWorkingDays(dataInicial, quantidadeDiasUteis);
  } catch (e) {
    return handleScalarError("ADD_WORKING_DAYS", e);
  }
}

/**
 * Retorna uma lista com N dias uteis a partir de uma data inicial, excluindo finais de semana e feriados.
 * @customfunction WORKING_DAYS_FROM
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param quantidadeDiasUteis Quantidade de dias uteis a retornar.
 * @returns Tabela com data e dia da semana dos dias uteis.
 */
export async function WORKING_DAYS_FROM(dataInicial: any, quantidadeDiasUteis: number): Promise<any[][]> {
  try {
    await resolveToken();
    return await getWorkingDaysFromTable(dataInicial, quantidadeDiasUteis);
  } catch (e) {
    return handleTableError("WORKING_DAYS_FROM", e);
  }
}

/**
 * Retorna a PTAX D-1 de uma moeda, considerando feriados.
 * @customfunction PTAX_D1
 * @param moeda Codigo da moeda. Use USD, EUR, JPY ou CHF.
 * @param [dataReferencia] Data de referencia. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, usa hoje e busca D-1 util.
 * @param [campo] Campo retornado. Use venda (padrao), compra, paridadeCompra ou paridadeVenda.
 * @returns Cotacao PTAX solicitada.
 */
export async function PTAX_D1(moeda: string, dataReferencia?: any, campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await ptaxD1(moeda, dataReferencia, campo);
  } catch (e) {
    return handleScalarError("PTAX_D1", e);
  }
}

/**
 * Retorna a PTAX de uma moeda em uma data especifica.
 * @customfunction PTAX
 * @param moeda Codigo da moeda. Use USD, EUR, JPY ou CHF.
 * @param data Data da cotacao. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [campo] Campo retornado. Use venda (padrao), compra, paridadeCompra ou paridadeVenda.
 * @returns Cotacao PTAX da data informada.
 */
export async function PTAX(moeda: string, data: any, campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await ptax(moeda, data, campo);
  } catch (e) {
    return handleScalarError("PTAX", e);
  }
}

/**
 * Retorna o cambio spot mais recente para uma moeda ou par.
 * @customfunction SPOT_FX
 * @param moedaOuPar Codigo da moeda ou par. Ex: USD, EUR, USD-BRL ou EUR/BRL.
 * @param [campo] Campo retornado. Use bid (padrao), ask, maxima ou minima.
 * @returns Cotacao spot solicitada.
 */
export async function SPOT_FX(moedaOuPar: string, campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await spotFx(moedaOuPar, campo);
  } catch (e) {
    return handleScalarError("SPOT_FX", e);
  }
}

/**
 * Retorna o CDI atual.
 * @customfunction CDI
 * @param [campo] Campo retornado. Use anualizado (padrao), diario ou valor.
 * @returns CDI atual conforme o campo escolhido.
 */
export async function CDI(campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await cdi(campo);
  } catch (e) {
    return handleScalarError("CDI", e);
  }
}

/**
 * Retorna o IPCA atual.
 * @customfunction IPCA
 * @param [campo] Campo retornado. Use mensal (padrao), ano ou 12m.
 * @returns IPCA atual conforme o campo escolhido.
 */
export async function IPCA(campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await ipca(campo);
  } catch (e) {
    return handleScalarError("IPCA", e);
  }
}

/**
 * Retorna taxa CDI/pre implicita interpolada para uma data de vencimento.
 * @customfunction CDI_IMPLICITO
 * @param dataVencimento Data alvo. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [campo] Campo retornado. Use taxaAnual (padrao), fatorAnual ou fatorDiario.
 * @returns Taxa ou fator interpolado da curva DI1.
 */
export async function CDI_IMPLICITO(dataVencimento: any, campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await cdiImplicito(dataVencimento, campo);
  } catch (e) {
    return handleScalarError("CDI_IMPLICITO", e);
  }
}

/**
 * Retorna IPCA implicito interpolado para uma data de vencimento.
 * @customfunction IPCA_IMPLICITO
 * @param dataVencimento Data alvo. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [campo] Campo retornado. Use taxaAnual (padrao), fatorAnual ou fatorDiario.
 * @returns Taxa ou fator interpolado da curva de IPCA implicito.
 */
export async function IPCA_IMPLICITO(dataVencimento: any, campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await ipcaImplicito(dataVencimento, campo);
  } catch (e) {
    return handleScalarError("IPCA_IMPLICITO", e);
  }
}

/**
 * Calcula forward entre duas datas para uma curva de mercado.
 * @customfunction FORWARD_CURVE
 * @param curva Curva usada no calculo. Use CDI/DI1, IPCA/ImplicitIpca, DAP, DDI, DOL ou EUR.
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param dataFinal Data final. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [campo] Campo retornado. Use taxaAnual (padrao), taxaPeriodo, fator, taxaInicial ou taxaFinal.
 * @returns Forward da curva conforme o campo escolhido.
 */
export async function FORWARD_CURVE(curva: string, dataInicial: any, dataFinal: any, campo?: string): Promise<number | string> {
  try {
    await resolveToken();
    return await forwardCurve(curva, dataInicial, dataFinal, campo);
  } catch (e) {
    return handleScalarError("FORWARD_CURVE", e);
  }
}

/**
 * Calcula termo BMF entre duas datas para uma curva.
 * @customfunction BMF_TERM
 * @param curva Curva usada no calculo. Use DI/CDI, DAP, Dollar/Dolar, Euro ou ImpliedIpca/IPCA.
 * @param dataInicial Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param dataFinal Data final. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd.
 * @param [campo] Campo retornado. Use taxaPeriodo (padrao), taxaAnualizada, taxaInicial ou taxaFinal.
 * @param [dataReferencia] Data de referencia da curva. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd. Se vazio, usa ultimo dado disponivel.
 * @returns Termo BMF conforme o campo escolhido.
 */
export async function BMF_TERM(curva: string, dataInicial: any, dataFinal: any, campo?: string, dataReferencia?: any): Promise<number | string> {
  try {
    await resolveToken();
    return await bmfTerm(curva, dataInicial, dataFinal, campo, dataReferencia);
  } catch (e) {
    return handleScalarError("BMF_TERM", e);
  }
}

CustomFunctions.associate("PRICE_FX", PRICE_FX);
CustomFunctions.associate("MTM_FX", MTM_FX);
CustomFunctions.associate("IMPLIED_VOL_FX", IMPLIED_VOL_FX);
CustomFunctions.associate("LOAN_DEBT_FLOW", LOAN_DEBT_FLOW);
CustomFunctions.associate("HOLIDAYS", HOLIDAYS);
CustomFunctions.associate("WORKING_DAYS", WORKING_DAYS);
CustomFunctions.associate("WORKING_DAYS_COUNT", WORKING_DAYS_COUNT);
CustomFunctions.associate("ADD_WORKING_DAYS", ADD_WORKING_DAYS);
CustomFunctions.associate("WORKING_DAYS_FROM", WORKING_DAYS_FROM);
CustomFunctions.associate("PTAX_D1", PTAX_D1);
CustomFunctions.associate("PTAX", PTAX);
CustomFunctions.associate("SPOT_FX", SPOT_FX);
CustomFunctions.associate("CDI", CDI);
CustomFunctions.associate("IPCA", IPCA);
CustomFunctions.associate("CDI_IMPLICITO", CDI_IMPLICITO);
CustomFunctions.associate("IPCA_IMPLICITO", IPCA_IMPLICITO);
CustomFunctions.associate("FORWARD_CURVE", FORWARD_CURVE);
CustomFunctions.associate("BMF_TERM", BMF_TERM);